import EventClient from "@2u-landing-pages/utils/event-client";
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { CustomCSSProperties, TabsProps } from "./Tabs.type";
import TabsNavigation from "./TabsNavigation";
// @ts-ignore
import styles from "./Tabs.module.scss";

const Hero = lazy(() => import("@2u-landing-pages/templates/Hero").then((m) => ({ default: m.Hero })));
const Bento = lazy(() => import("@2u-landing-pages/templates/Bento").then((m) => ({ default: m.Bento })));
const Split = lazy(() => import("@2u-landing-pages/templates/Split").then((m) => ({ default: m.Split })));
const Flex = lazy(() => import("@2u-landing-pages/templates/Flex").then((m) => ({ default: m.Flex })));

const TAB_COMPONENTS: Record<string, React.ComponentType<any>> = {
  hero: Hero,
  bento: Bento,
  split: Split,
  flex: Flex,
};

type PaddingMap = { [index: string]: string };

const paddingMap: PaddingMap = {
  "Large (64/32)": "large",
  "Medium (48/24)": "medium",
  "Small (32/16)": "small",
  "X-Small (24/12)": "xsmall",
};

const TabContent: React.FC<{
  tab: any;
  inspector: any;
  programLogo: any;
  isLoading: boolean;
  phoneNumber?: string;
}> = ({ tab, inspector, programLogo, isLoading, phoneNumber }) => {
  if (!tab?.fields?.tab_content) return null;

  const { tab_content } = tab.fields;

  return (
    tab_content &&
    tab_content.map((entry: any) => {
      if (!entry.sys.contentType.sys.id) return null;

      const type = entry.sys.contentType.sys.id;
      const Component = TAB_COMPONENTS[type];

      return Component ? (
        <Component
          {...entry.fields}
          key={entry.sys.id}
          id={entry.sys.id}
          inspector={inspector}
          programLogo={programLogo}
          isLoading={isLoading}
          phoneNumber={phoneNumber}
        />
      ) : null;
    })
  );
};

export const Tabs: React.FC<TabsProps> = ({
  id,
  content,
  inspector,
  programLogo,
  isLoading,
  phoneNumber,
  tabs_navigation_top_spacing = "Large (64/32)",
  tabs_navigation_bottom_spacing = "Large (64/32)",
  tabs_section_background,
  tab_button_size,
  tabs_border_color,
  button_style,
  animation = "fade",
  adaptive_height,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const sliderRef = useRef<Slider | null>(null);
  const [sliderReady, setSliderReady] = useState(0);

  // when Slider mounts we get the instance and bump state so effects that depend on the slider can run.
  const setSliderRef = useCallback((slider: Slider | null) => {
    sliderRef.current = slider;
    if (slider) setSliderReady((s) => s + 1);
  }, []);

  // `light` has a dark grey background color but design wants to see a black background color
  const backgroundColor = button_style === "light" ? "dark-default" : `${button_style}-inverse`;
  // set up styles by checking if the fields are defined
  const customTabStyles: CustomCSSProperties = {};
  if (tabs_section_background) customTabStyles["--tabs-section-background"] = `var(${tabs_section_background})`;
  if (tabs_border_color) customTabStyles["--tabs-border-color"] = `var(${tabs_border_color})`;
  if (button_style) customTabStyles["--tabs-navigation-background"] = `var(--action-button-${backgroundColor})`;

  useEffect(() => {
    setActiveTab(0);
  }, [adaptive_height]);

  const handleInit = useCallback(() => {
    if (adaptive_height && sliderRef.current?.slickGoTo) {
      setTimeout(() => {
        sliderRef.current?.slickGoTo?.(activeTab || 0);
      }, 0);
    }
  }, [adaptive_height, activeTab]);

  // ResizeObserver — runs when slider ref is set (sliderReady) or activeTab changes. Resizes on lazy loading.
  useEffect(() => {
    if (!adaptive_height || typeof window === "undefined") return;

    let ro: ResizeObserver | undefined;
    let rafId: number | undefined;

    const setup = () => {
      if (ro) return;
      const inner = (sliderRef.current as { innerSlider?: { list: HTMLElement; adaptHeight: () => void } } | null)
        ?.innerSlider;
      const slide = inner?.list?.querySelector<HTMLElement>(`[data-index="${activeTab}"]`);
      if (!slide || !inner) return;
      ro = new ResizeObserver(() => inner.adaptHeight());
      ro.observe(slide);
    };
    setup();
    if (!ro) rafId = requestAnimationFrame(setup);
    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      ro?.disconnect();
    };
  }, [adaptive_height, activeTab, sliderReady]);

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLLIElement>, index: number, label?: string) => {
      e.preventDefault();
      e.stopPropagation();

      const buttonId = (e.target as HTMLElement).id;

      EventClient.elementClicked({
        webElement: {
          elementType: "Tab Navigation Button",
          htmlId: buttonId,
          location: "Tab Component",
          position: "Tab Navigation",
          text: label || "",
        },
      });

      // find all siblings of the clicked tab
      const siblings = Array.from((e.currentTarget as HTMLElement).parentElement?.children || []);
      siblings.forEach((sibling) => sibling.classList.remove(styles.active));
      // add active class to the clicked tab
      (e.currentTarget as HTMLElement).classList.add(styles.active);

      if (sliderRef.current && index !== activeTab) {
        sliderRef.current.slickGoTo(index);
        setActiveTab(index);
      }
    },
    [activeTab],
  );

  const settings = {
    dots: false,
    infinite: true,
    speed: animation === "none" ? 0 : 250,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: adaptive_height,
    easing: "ease-in-out",
    fade: ["fade", "none"].includes(animation),
    beforeChange: (_: number, next: number) => setActiveTab(next),
    arrows: false,
    swipe: false,
    onInit: handleInit,
  };

  if (!content?.length) return null;

  return (
    <section id={id} className={styles.tabs} style={customTabStyles}>
      <TabsNavigation
        content={content}
        pendingTab={activeTab}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        button_style={button_style || "primary"}
        paddingTop={paddingMap[tabs_navigation_top_spacing]}
        paddingBottom={paddingMap[tabs_navigation_bottom_spacing]}
        tab_button_size={tab_button_size}
      />

      <div className={styles.tabPanel}>
        <Slider key={`tabs-slider${adaptive_height && "-adaptive"}`} ref={setSliderRef} {...settings}>
          {content.map((tab, index) => (
            <div
              key={tab?.fields?.tab_label || index}
              role="tabpanel"
              id={`tabpanel-${index}`}
              className={styles.tabPanelItem}
              aria-labelledby={`tab-${index}`}
            >
              <Suspense fallback={<div className={styles.tabPanelLoading}>Loading...</div>}>
                <TabContent
                  tab={tab}
                  inspector={inspector}
                  programLogo={programLogo}
                  isLoading={isLoading}
                  phoneNumber={phoneNumber}
                />
              </Suspense>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};
